import React, { useEffect, useState } from "react";
import { updateBasicDetails } from "../../../services/UserServices/UserService";
import { notifyError, notifyTopRight } from "../Common/Toaster";
import { Modal } from "react-bootstrap";

const COUNTRY_CODES = [
  { label: "Singapore (+65)", value: "65" },
  { label: "Malaysia (+60)", value: "60" },
  { label: "Australia (+61)", value: "61" },
  { label: "Hong Kong (+852)", value: "852" },
];

const normalizeCountryCode = (code) =>
  code ? String(code).replace(/^\+/, "") : "";

export default function UserUpdate({ show, onHide, table, user }) {
  const errorsObj = { name: "", age: "", countryCode: "" };
  const [errors, setErrors] = useState(errorsObj);

  const [formData, setFormData] = useState({
    userId: user?._id || "",
    name: user?.name || "",
    email: user?.email || "",
    age: user?.age ?? "",
    countryCode: normalizeCountryCode(user?.countryCode),
    phoneNumber: user?.phoneNumber || "",
  });

  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (!show) return;
    setFormData({
      userId: user?._id || "",
      name: user?.name || "",
      email: user?.email || "",
      age: user?.age ?? "",
      countryCode: normalizeCountryCode(user?.countryCode),
      phoneNumber: user?.phoneNumber || "",
    });
    setErrors(errorsObj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user?._id]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errorsObj };

    if (formData?.name.trim() === "") {
      errorObj.name = "Name is required";
      error = true;
    }

    const ageRaw = formData?.age;
    const ageTrimmed = String(ageRaw ?? "").trim();
    if (ageTrimmed) {
      const ageNum = Number(ageTrimmed);
      if (!Number.isFinite(ageNum) || !Number.isInteger(ageNum)) {
        errorObj.age = "Age must be a whole number";
        error = true;
      } else if (ageNum < 18 || ageNum > 100) {
        errorObj.age = "Age must be between 18 and 100";
        error = true;
      }
    }

    const phoneTrimmed = formData?.phoneNumber?.trim() || "";
    if (phoneTrimmed && !formData?.countryCode) {
      errorObj.countryCode =
        "Country code is required when phone number is entered";
      error = true;
    }

    setErrors(errorObj);
    if (error) return;

    const payload = {
      userId: formData.userId,
      name: formData.name,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
    };

    if (String(formData?.age ?? "").trim()) {
      payload.age = Number(String(formData.age).trim());
    }

    if (phoneTrimmed) {
      payload.countryCode = normalizeCountryCode(formData.countryCode);
    }

    setLoader(true);
    try {
      const response = await updateBasicDetails(payload);
      notifyTopRight(response?.data?.message || "User updated successfully");
      onHide();
      table();
    } catch (err) {
      notifyError(err?.response?.data?.message || "Failed to update user");
    } finally {
      setLoader(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <form onSubmit={onSubmit}>
        <div className="modal-header">
          <h4 className="modal-title fs-20">Update User Details</h4>
        </div>
        <div className="modal-body">
          <div className="form-group mb-3">
            <label className="text-black font-w500">Name</label>
            <input
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleInput}
            />
            {errors.name && (
              <div className="text-danger fs-12">{errors.name}</div>
            )}
          </div>

          <div className="form-group mb-3">
            <label className="text-black font-w500">Email</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleInput}
            />
          </div>

          <div className="form-group mb-3">
            <label className="text-black font-w500">Age</label>
            <input
              type="number"
              className="form-control"
              name="age"
              value={formData.age}
              onChange={handleInput}
              min={18}
              max={100}
            />
            {errors.age && <div className="text-danger fs-12">{errors.age}</div>}
          </div>

          <div className="form-group mb-3">
            <label className="text-black font-w500">Country Code</label>
            <select
              className="form-control"
              name="countryCode"
              value={formData.countryCode}
              onChange={handleInput}
            >
              <option value="">Select country code</option>
              {COUNTRY_CODES.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.countryCode && (
              <div className="text-danger fs-12">{errors.countryCode}</div>
            )}
          </div>

          <div className="form-group mb-3">
            <label className="text-black font-w500">Phone Number</label>
            <input
              className="form-control"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInput}
              maxLength={15}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" onClick={onHide} className="btn btn-danger">
            <i className="flaticon-delete-1"></i> Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {loader ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
